import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, MessageCircle, Plus, Trash2 } from 'lucide-react';
import { MAX_ASSUNTOS_WIDGET, POSICOES_WIDGET, TAMANHO_ASSUNTO_WIDGET } from '@malgoos/shared';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Alternador, AreaSensivel, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { CampoVerticais, CartaoVerticais, verticaisDaCriacao, type ValorVerticais } from '../../componentes/verticais';
import { ErroRequisicao, del, get, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { EditorHorario, HORARIO_PADRAO } from './Filas';
import type { Fila, Horario } from './tipos';

/** Item 11 do lote de 19/10: as 6 cores de destaque do widget (as do `Selo`, a mesma lista do backend). */
const CORES = ['acento', 'azul', 'ciano', 'roxo', 'laranja', 'verde'] as const;
type CorWidget = (typeof CORES)[number];
type PosicaoWidget = (typeof POSICOES_WIDGET)[number];

interface AssuntoWidget {
  id: string;
  rotulo: string;
  fila_id: string | null;
}

/** Horário da fila enquanto a pessoa edita na gaveta do widget. */
interface EdicaoHorario {
  usar: boolean;
  horario: Horario;
  mensagem: string;
}

interface Widget {
  id: string;
  nome: string;
  dominios: string[];
  fila_id: string | null;
  fila_nome: string | null;
  cor: CorWidget;
  posicao: PosicaoWidget;
  saudacao: string | null;
  pedir_dados: boolean;
  texto_lgpd: string | null;
  assuntos: AssuntoWidget[];
  ativo: boolean;
  conversas_ativas: number;
  created_at: string;
}

interface ListaWidgets {
  itens: Widget[];
  /** Endereço do `chat.js` (o da equipe; no Sandbox, o do portal do Sandbox). Null: o Sandbox sem endereço configurado. */
  script_url: string | null;
}

const CAMINHO = '/conversas/widgets';
const CLASSE_AREA = 'w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco';

/** Copia para a área de transferência, com plano B para contexto sem permissão do navegador. */
async function copiarTexto(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    try {
      const area = document.createElement('textarea');
      area.value = texto;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      const feito = document.execCommand('copy');
      area.remove();
      return feito;
    } catch {
      return false;
    }
  }
}

export const codigoDoWidget = (scriptUrl: string, id: string) => `<script src="${scriptUrl}" data-widget="${id}" async></script>`;

/** O código que o site cola, com o botão de copiar (só depois de salvo: o id vai no código). */
function CodigoIncorporar({ widget, scriptUrl }: { widget: Widget | null; scriptUrl: string | null }) {
  const { t } = useTranslation('conversas');
  const toast = useToast();
  if (!widget) return <p className="text-xs text-tinta-3">{t('site.salveParaCodigo')}</p>;
  if (!scriptUrl) return <p className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2">{t('site.semEndereco')}</p>;
  const codigo = codigoDoWidget(scriptUrl, widget.id);
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <code className="numero block min-w-0 rounded-lg border border-borda bg-sup-2 p-3 text-xs break-all" data-codigo-widget>
        {codigo}
      </code>
      <Botao tamanho="sm" className="w-fit" icone={<Copy className="size-4" />} onClick={async () => ((await copiarTexto(codigo)) ? toast.sucesso(t('site.copiado')) : toast.erro(t('site.naoCopiado')))}>
        {t('site.copiar')}
      </Botao>
      <span className="text-xs text-tinta-3">{t('site.ajudaCodigo')}</span>
    </div>
  );
}

/** Ficha 2026-11-04: o menu de assuntos do widget. Cada linha tem o id dela (nova: gerado aqui), para o `key` e o servidor. */
function EditorAssuntos({ assuntos, aoMudar, filas, erro, desabilitado }: { assuntos: AssuntoWidget[]; aoMudar: (a: AssuntoWidget[]) => void; filas: Fila[]; erro?: string; desabilitado: boolean }) {
  const { t } = useTranslation('conversas');
  const mudar = (id: string, parte: Partial<AssuntoWidget>) => aoMudar(assuntos.map((a) => (a.id === id ? { ...a, ...parte } : a)));
  return (
    <fieldset className="flex min-w-0 flex-col gap-2">
      <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('site.assuntos')}</legend>
      <span className="text-xs text-tinta-3">{t('site.ajudaAssuntos')}</span>
      {assuntos.map((a, i) => (
        <div key={a.id} className="flex min-w-0 flex-wrap items-end gap-2 rounded-lg border border-borda bg-sup-2 p-2">
          <div className="min-w-40 flex-1">
            <Rotulo id={`widget-assunto-${a.id}`} rotulo={t('site.assuntoRotulo')}>
              <Entrada id={`widget-assunto-${a.id}`} value={a.rotulo} maxLength={TAMANHO_ASSUNTO_WIDGET} disabled={desabilitado} onChange={(e) => mudar(a.id, { rotulo: e.target.value })} />
            </Rotulo>
          </div>
          <div className="min-w-40 flex-1">
            <Rotulo id={`widget-assunto-fila-${a.id}`} rotulo={t('site.assuntoFila')}>
              <Selecao
                id={`widget-assunto-fila-${a.id}`}
                disabled={desabilitado}
                opcoes={[{ valor: '', rotulo: t('site.assuntoFilaPadrao') }, ...filas.map((f) => ({ valor: f.id, rotulo: f.nome }))]}
                value={a.fila_id ?? ''}
                onChange={(e) => mudar(a.id, { fila_id: e.target.value || null })}
              />
            </Rotulo>
          </div>
          <Botao variante="fantasma" tamanho="sm" disabled={desabilitado} aria-label={t('site.assuntoRemover', { n: i + 1 })} icone={<Trash2 className="size-4" />} onClick={() => aoMudar(assuntos.filter((x) => x.id !== a.id))} />
        </div>
      ))}
      {erro && <p className="text-xs text-perigo">{erro}</p>}
      {assuntos.length < MAX_ASSUNTOS_WIDGET && (
        <Botao tamanho="sm" className="w-fit" disabled={desabilitado} icone={<Plus className="size-4" />} onClick={() => aoMudar([...assuntos, { id: crypto.randomUUID(), rotulo: '', fila_id: null }])}>
          {t('site.assuntoAdicionar')}
        </Botao>
      )}
    </fieldset>
  );
}

/** Ficha 2026-11-04: o horário do chat é o da fila dele (uma regra só); aqui ele se edita sem sair do cadastro do widget. */
function HorarioDoWidget({ filaId, fila, valor, aoMudar, erros, desabilitado }: { filaId: string; fila: Fila | null; valor: EdicaoHorario; aoMudar: (v: EdicaoHorario) => void; erros: Record<string, string>; desabilitado: boolean }) {
  const { t } = useTranslation('conversas');
  return (
    <fieldset className="flex min-w-0 flex-col gap-2">
      <legend className="mb-1.5 text-sm font-medium text-tinta-2">{t('t5b.horario.titulo')}</legend>
      {!filaId && <span className="text-xs text-tinta-3">{t('site.horarioSemFila')}</span>}
      {fila && (
        <>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-borda bg-sup-2 px-3 py-2">
            <label htmlFor="widget-horario" className="text-sm text-tinta-2">
              {t('t5b.horario.usar')}
            </label>
            <Alternador id="widget-horario" rotulo={t('t5b.horario.usar')} marcado={valor.usar} desabilitado={desabilitado} aoMudar={(usar) => aoMudar({ ...valor, usar })} />
          </div>
          <span className="text-xs text-tinta-3">{t('site.horarioAjuda', { fila: fila.nome })}</span>
          {erros.horario && <p className="text-xs text-perigo">{erros.horario}</p>}
          {valor.usar && <EditorHorario horario={valor.horario} aoMudar={(horario) => aoMudar({ ...valor, horario })} desabilitado={desabilitado} />}
          {valor.usar && (
            <Rotulo id="widget-fora" rotulo={t('t5b.horario.mensagem')} ajuda={t('t5b.horario.ajudaMensagem')} erro={erros.mensagem_fora_horario}>
              <AreaSensivel id="widget-fora" rotulo={t('t5b.horario.mensagem')} rows={2} maxLength={1000} disabled={desabilitado} value={valor.mensagem} onChange={(e) => aoMudar({ ...valor, mensagem: e.target.value })} className={CLASSE_AREA} />
            </Rotulo>
          )}
        </>
      )}
    </fieldset>
  );
}

function FormWidget({ widget, scriptUrl, aoConcluir }: { widget: Widget | null; scriptUrl: string | null; aoConcluir: () => void }) {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const s = useSessao();
  const podeEditar = s.pode('conversas.configurar');
  const [nome, setNome] = useState(widget?.nome ?? '');
  const [dominios, setDominios] = useState((widget?.dominios ?? []).join('\n'));
  const [fila, setFila] = useState(widget?.fila_id ?? '');
  const [cor, setCor] = useState<CorWidget>(widget?.cor ?? 'acento');
  const [posicao, setPosicao] = useState<PosicaoWidget>(widget?.posicao ?? 'direita');
  const [assuntos, setAssuntos] = useState<AssuntoWidget[]>(widget?.assuntos ?? []);
  // Null: a pessoa não mexeu no horário e a fila não é gravada. Trocar de fila descarta o que estava sendo editado.
  const [edicaoHorario, setEdicaoHorario] = useState<EdicaoHorario | null>(null);
  const [saudacao, setSaudacao] = useState(widget?.saudacao ?? '');
  const [pedirDados, setPedirDados] = useState(widget?.pedir_dados ?? true);
  const [lgpd, setLgpd] = useState(widget?.texto_lgpd ?? '');
  const [ativo, setAtivo] = useState(widget?.ativo ?? true);
  // Ficha 2026-10-22: as verticais do widget já na criação (null = o escopo de quem cria, como antes).
  const [verticais, setVerticais] = useState<ValorVerticais>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  useValidacaoLocal(erros, erroGeral);
  const filas = useQuery({ queryKey: ['/conversas/filas', 'todas'], queryFn: () => getTodos<Fila>('/conversas/filas') });
  const filaEscolhida = filas.data?.itens.find((f) => f.id === fila) ?? null;
  const valorHorario: EdicaoHorario = edicaoHorario ?? { usar: !!filaEscolhida?.horario, horario: filaEscolhida?.horario ?? HORARIO_PADRAO, mensagem: filaEscolhida?.mensagem_fora_horario ?? '' };
  const listaDominios = dominios
    .split('\n')
    .map((d) => d.trim())
    .filter(Boolean);

  const salvar = useMutation({
    mutationFn: async () => {
      // A fila primeiro: repetir o salvamento depois de uma recusa do widget não muda nada nela e não cria widget duplicado.
      if (edicaoHorario && filaEscolhida) {
        await patch(`/conversas/filas/${filaEscolhida.id}`, { horario: edicaoHorario.usar ? edicaoHorario.horario : null, mensagem_fora_horario: edicaoHorario.mensagem.trim() || null });
      }
      const corpo = { nome: nome.trim(), dominios: listaDominios, fila_id: fila || null, cor, posicao, assuntos: assuntos.map((a) => ({ ...a, rotulo: a.rotulo.trim() })), saudacao: saudacao.trim() || null, pedir_dados: pedirDados, texto_lgpd: lgpd.trim() || null, ativo };
      return widget ? patch(`${CAMINHO}/${widget.id}`, corpo) : post(CAMINHO, { ...corpo, ...verticaisDaCriacao(verticais) });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [CAMINHO] });
      await qc.invalidateQueries({ queryKey: ['/conversas/filas'] });
      toast.sucesso(tn('comum.salvo'));
      aoConcluir();
    },
    onError: (e) => {
      void qc.invalidateQueries({ queryKey: ['/conversas/filas'] });
      if (e instanceof ErroRequisicao) {
        const porCampo = e.porCampo();
        setErros(porCampo);
        // A recusa das verticais (campo `empresas`) não tem campo próprio aqui: vai no aviso geral.
        setErroGeral(Object.keys(porCampo).some((k) => k !== 'empresas') ? null : mensagemDeErro(e));
      } else setErroGeral(mensagemDeErro(e));
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <form
        id="form-widget"
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setErroGeral(null);
          if (!nome.trim()) {
            setErros({ nome: tn('erros.obrigatorioCampo', { campo: t('site.nome') }) });
            return;
          }
          if (assuntos.some((a) => !a.rotulo.trim())) {
            setErros({ assuntos: t('site.assuntoVazio') });
            return;
          }
          setErros({});
          salvar.mutate();
        }}
      >
        {erroGeral && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {erroGeral}
          </p>
        )}
        <Rotulo id="widget-nome" rotulo={t('site.nome')} obrigatorio ajuda={t('site.ajudaNome')} erro={erros.nome}>
          <Entrada id="widget-nome" value={nome} maxLength={80} disabled={!podeEditar} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        <Rotulo id="widget-dominios" rotulo={t('site.dominios')} ajuda={t('site.ajudaDominios')} erro={erros.dominios}>
          <AreaSensivel id="widget-dominios" rotulo={t('site.dominios')} rows={3} disabled={!podeEditar} value={dominios} onChange={(e) => setDominios(e.target.value)} className={CLASSE_AREA} />
        </Rotulo>
        {!listaDominios.length && <p className="text-xs text-tinta-3">{t('site.semDominio')}</p>}
        <Rotulo id="widget-fila" rotulo={t('site.fila')} ajuda={t('site.ajudaFila')} erro={erros.fila_id}>
          <Selecao
            id="widget-fila"
            disabled={!podeEditar}
            opcoes={[{ valor: '', rotulo: t('site.filaPadrao') }, ...(filas.data?.itens ?? []).map((f) => ({ valor: f.id, rotulo: f.nome }))]}
            value={fila}
            onChange={(e) => {
              setFila(e.target.value);
              setEdicaoHorario(null);
            }}
          />
        </Rotulo>
        <HorarioDoWidget filaId={fila} fila={filaEscolhida} valor={valorHorario} aoMudar={setEdicaoHorario} erros={erros} desabilitado={!podeEditar} />
        <Rotulo id="widget-cor" rotulo={t('site.cor')} erro={erros.cor}>
          <Selecao id="widget-cor" disabled={!podeEditar} opcoes={CORES.map((c) => ({ valor: c, rotulo: t(`site.cores.${c}`) }))} value={cor} onChange={(e) => setCor(e.target.value as CorWidget)} />
        </Rotulo>
        <Rotulo id="widget-posicao" rotulo={t('site.posicao')} ajuda={t('site.ajudaPosicao')} erro={erros.posicao}>
          <Selecao id="widget-posicao" disabled={!podeEditar} opcoes={POSICOES_WIDGET.map((p) => ({ valor: p, rotulo: t(`site.${p}`) }))} value={posicao} onChange={(e) => setPosicao(e.target.value as PosicaoWidget)} />
        </Rotulo>
        <EditorAssuntos assuntos={assuntos} aoMudar={(lista) => {
            setAssuntos(lista);
            setErros((atual) => Object.fromEntries(Object.entries(atual).filter(([campo]) => campo !== 'assuntos')));
          }} filas={filas.data?.itens ?? []} erro={erros.assuntos} desabilitado={!podeEditar} />
        <Rotulo id="widget-saudacao" rotulo={t('site.saudacao')} ajuda={t('site.ajudaSaudacao')} erro={erros.saudacao}>
          <AreaSensivel id="widget-saudacao" rotulo={t('site.saudacao')} rows={2} maxLength={500} disabled={!podeEditar} value={saudacao} onChange={(e) => setSaudacao(e.target.value)} className={CLASSE_AREA} />
        </Rotulo>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3 rounded-lg border border-borda bg-sup-2 px-3 py-2">
            <label htmlFor="widget-dados" className="text-sm text-tinta-2">
              {t('site.pedirDados')}
            </label>
            <Alternador id="widget-dados" rotulo={t('site.pedirDados')} marcado={pedirDados} desabilitado={!podeEditar} aoMudar={setPedirDados} />
          </div>
          <span className="text-xs text-tinta-3">{t('site.ajudaPedirDados')}</span>
        </div>
        <Rotulo id="widget-lgpd" rotulo={t('site.lgpd')} ajuda={t('site.ajudaLgpd')} erro={erros.texto_lgpd}>
          <AreaSensivel id="widget-lgpd" rotulo={t('site.lgpd')} rows={3} maxLength={1000} disabled={!podeEditar} value={lgpd} onChange={(e) => setLgpd(e.target.value)} className={CLASSE_AREA} />
        </Rotulo>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-borda bg-sup-2 px-3 py-2">
          <label htmlFor="widget-ativo" className="text-sm font-medium text-tinta-2">
            {t('site.ativo')}
          </label>
          <Alternador id="widget-ativo" rotulo={t('site.ativo')} marcado={ativo} desabilitado={!podeEditar} aoMudar={setAtivo} />
        </div>
        {!widget && <CampoVerticais valor={verticais} aoMudar={setVerticais} desabilitado={!podeEditar} />}
      </form>
      <section className="flex min-w-0 flex-col gap-2" aria-labelledby="widget-codigo">
        <h3 id="widget-codigo" className="text-sm font-medium text-tinta-2">
          {t('site.codigo')}
        </h3>
        <CodigoIncorporar widget={widget} scriptUrl={scriptUrl} />
      </section>
      {widget && <CartaoVerticais entidade="conversas.widget" registroId={widget.id} podeEditar={podeEditar} comHistorico />}
    </div>
  );
}

/**
 * Item 11 do lote de 19/10 (ficha "Chat do site"): Comercial > Conversas > Chat do site. Um widget por site (ou grupo de
 * sites): domínios permitidos, fila, cor, saudação, dados pedidos e aceite da LGPD, e o código que o site cola. As
 * conversas chegam na caixa Site. Mesmo padrão da aba Filas: tabela, gaveta e erro por campo.
 */
export function ChatSite() {
  const { t } = useTranslation('conversas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<Widget | 'novo' | null>(null);
  const [remover, setRemover] = useState(false);
  const atual = aberto && aberto !== 'novo' ? aberto : null;
  const podeEditar = s.pode('conversas.configurar');
  const lista = useQuery({ queryKey: [CAMINHO], queryFn: () => get<ListaWidgets>(CAMINHO) });

  const excluir = useMutation({
    mutationFn: (id: string) => del(`${CAMINHO}/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [CAMINHO] });
      toast.sucesso(tn('comum.removido'));
      setRemover(false);
      setAberto(null);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const colunas: Coluna<Widget>[] = [
    {
      chave: 'nome',
      rotulo: t('site.nome'),
      principal: true,
      render: (w) => (
        <span className="flex min-w-0 items-center gap-2">
          <TextoCortado className="min-w-0 font-medium">{w.nome}</TextoCortado>
          {!w.ativo && <Selo>{t('site.inativo')}</Selo>}
        </span>
      ),
    },
    { chave: 'dominios', rotulo: t('site.dominios'), render: (w) => (w.dominios.length ? <TextoCortado>{w.dominios.join(', ')}</TextoCortado> : t('site.semDominio')) },
    { chave: 'fila_nome', rotulo: t('site.fila'), render: (w) => w.fila_nome ?? t('site.filaPadrao') },
    { chave: 'cor', rotulo: t('site.cor'), render: (w) => <Selo cor={w.cor}>{t(`site.cores.${w.cor}`)}</Selo> },
    { chave: 'conversas_ativas', rotulo: t('site.conversasAtivas'), mono: true, render: (w) => <span className="numero">{w.conversas_ativas}</span> },
  ];

  return (
    <>
      {(podeEditar || s.pode('conversas.excluir')) && (
        <AcaoPrimaria>
          {s.pode('conversas.excluir') && <BotaoLixeira entidades={['conversas.widget']} />}
          {podeEditar && (
            <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
              {t('site.novo')}
            </Botao>
          )}
        </AcaoPrimaria>
      )}
      <Tabela<Widget>
        tela="conversas.chat-site"
        caminho={CAMINHO}
        colunas={colunas}
        aoClicar={(w) => setAberto(w)}
        vazio={
          <Vazio
            icone={<MessageCircle className="size-5" aria-hidden />}
            titulo={t('site.vazioTitulo')}
            texto={t('site.vazio')}
            acao={
              podeEditar ? (
                <Botao icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
                  {t('site.novo')}
                </Botao>
              ) : undefined
            }
          />
        }
      />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? atual.nome : t('site.novo')}
        rodape={
          <>
            {atual && s.pode('conversas.excluir') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setRemover(true)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            {podeEditar && (
              <Botao variante="primario" type="submit" form="form-widget">
                {tn('comum.salvar')}
              </Botao>
            )}
          </>
        }
      >
        {aberto && <FormWidget key={atual?.id ?? 'novo'} widget={atual} scriptUrl={lista.data?.script_url ?? null} aoConcluir={() => setAberto(null)} />}
      </Drawer>
      <Confirmar aberto={remover} aoFechar={() => setRemover(false)} aoConfirmar={() => atual && excluir.mutate(atual.id)} carregando={excluir.isPending} titulo={t('site.removerTitulo')} texto={t('site.removerTexto')} perigoso />
    </>
  );
}
